import { useState } from 'react';
import { History, Users } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface GhostMatch {
  personId: string;
  displayName: string;
  roundCount: number;
  matchType: 'name' | 'group';
  playedWith?: string[];
}

interface Props {
  open: boolean;
  onDismiss: () => void;
  onClaimed: () => void;
}

export function GhostClaimModal({ open, onDismiss, onClaimed }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [claimingId, setClaimingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ matches: GhostMatch[] }>({
    queryKey: ['/api/persons/ghost-search'],
    queryFn: async () => {
      const res = await fetch('/api/persons/ghost-search', { credentials: 'include' });
      if (!res.ok) throw new Error('Search failed');
      return res.json();
    },
    enabled: open && !!(user as any)?.firstName,
    staleTime: 30000,
  });

  const claimMutation = useMutation({
    mutationFn: async ({ personId, groupClaim }: { personId: string; groupClaim: boolean }) => {
      const url = groupClaim
        ? `/api/persons/claim/${personId}?groupClaim=true`
        : `/api/persons/claim/${personId}`;
      const res = await fetch(url, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || 'Claim failed');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/persons/ghost-search'] });
      toast({ title: '🎉 Rounds linked!', description: 'Your match history is now in your Ledger.' });
      onClaimed();
    },
    onError: (err: Error) => {
      toast({ title: 'Could not link rounds', description: err.message, variant: 'destructive' });
      setClaimingId(null);
    },
  });

  const matches = data?.matches ?? [];
  const nameMatches = matches.filter((m) => m.matchType === 'name');
  const groupMatches = matches.filter((m) => m.matchType === 'group');

  if (!open) return null;

  const renderMatchRow = (match: GhostMatch) => {
    const isPending = claimMutation.isPending && claimingId === match.personId;
    return (
      <div
        key={match.personId}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
          backgroundColor: 'rgba(255,255,255,0.07)',
          borderRadius: '10px',
          padding: '12px 14px',
          marginBottom: '10px',
        }}
        data-testid={`ghost-match-${match.personId}`}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: '#ffffff', fontWeight: 700, fontSize: '15px', margin: '0 0 2px' }}>
            {match.displayName}
          </p>
          <p style={{ color: 'rgba(250,204,21,0.85)', fontSize: '13px', margin: 0 }}>
            {match.roundCount} {match.roundCount === 1 ? 'round' : 'rounds'} waiting
          </p>
          {match.matchType === 'group' && match.playedWith && match.playedWith.length > 0 && (
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', margin: '4px 0 0' }}>
              Played with {match.playedWith.join(', ')}
            </p>
          )}
        </div>
        <button
          onClick={() => {
            setClaimingId(match.personId);
            claimMutation.mutate({ personId: match.personId, groupClaim: match.matchType === 'group' });
          }}
          disabled={claimMutation.isPending}
          data-testid={`button-claim-${match.personId}`}
          style={{
            height: '38px',
            padding: '0 16px',
            backgroundColor: isPending ? 'rgba(6,78,59,0.6)' : '#064E3B',
            border: '2px solid #FACC15',
            borderRadius: '8px',
            color: '#FACC15',
            fontSize: '14px',
            fontWeight: 800,
            cursor: claimMutation.isPending ? 'not-allowed' : 'pointer',
            flexShrink: 0,
          }}
        >
          {isPending ? '…' : "That's me"}
        </button>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '16px',
      }}
      data-testid="ghost-claim-modal"
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          backgroundColor: '#1e3a5f',
          borderRadius: '20px',
          padding: '28px',
          border: '2px solid #FACC15',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <button
          onClick={onDismiss}
          style={{
            position: 'absolute',
            top: '12px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '28px',
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
          data-testid="button-ghost-dismiss"
        >
          ×
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: 'rgba(250,204,21,0.15)',
            border: '2px solid #FACC15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <History style={{ width: '22px', height: '22px', color: '#FACC15' }} />
          </div>
          <p style={{ color: '#FACC15', fontWeight: 800, margin: 0, fontSize: '20px', lineHeight: 1.2 }}>
            Claim Your History
          </p>
        </div>

        <p style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, margin: '0 0 20px', fontSize: '14px', lineHeight: 1.5 }}>
          We found past rounds before you joined. Claim the one that's you to see your full history and stats.
        </p>

        <div style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: '12px', padding: '16px' }}>
          {isLoading && (
            <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', fontSize: '14px', margin: 0 }}>
              Searching for your rounds…
            </p>
          )}

          {!isLoading && matches.length === 0 && (
            <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', fontSize: '14px', margin: 0 }}>
              No unclaimed rounds found.
            </p>
          )}

          {nameMatches.length > 0 && (
            <>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>
                Found under your name
              </p>
              {nameMatches.map(renderMatchRow)}
            </>
          )}

          {groupMatches.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: nameMatches.length > 0 ? '12px 0 8px' : '0 0 8px' }}>
                <Users style={{ width: '13px', height: '13px', color: 'rgba(255,255,255,0.45)', flexShrink: 0 }} />
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                  Possible match from your group
                </p>
              </div>
              {groupMatches.map(renderMatchRow)}
            </>
          )}

          <button
            onClick={onDismiss}
            style={{
              width: '100%',
              height: '40px',
              backgroundColor: 'transparent',
              border: '1.5px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: 'rgba(255,255,255,0.55)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: matches.length > 0 ? '6px' : '0',
            }}
            data-testid="button-ghost-skip"
          >
            Not me — skip
          </button>
        </div>
      </div>
    </div>
  );
}
